import React, { useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@tectonics/utils/utils';
import { Label } from '@tectonics/components/ui/label';
import { Separator } from '@tectonics/components/ui/separator';

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex flex-col gap-6 border-0',
        'has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3',
        className
      )}
      {...props}
    />
  );
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        'mb-3 font-medium',
        'data-[variant=legend]:text-base',
        'data-[variant=label]:text-sm',
        className
      )}
      {...props}
    />
  );
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-7 data-[slot=checkbox-group]:gap-3 [&>[data-slot=field-group]]:gap-4',
        className
      )}
      {...props}
    />
  );
}

const fieldVariants = cva(
  'group/field data-[invalid=true]:text-destructive flex w-full gap-0',
  {
    variants: {
      orientation: {
        vertical: ['flex-col [&>*:not(select[aria-hidden])]:w-full [&>.sr-only]:w-auto'],
        horizontal: [
          'flex-row items-center',
          '[&>[data-slot=field-label]]:flex-auto',
          'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px has-[>[data-slot=field-content]]:items-start',
        ],
        responsive: [
          '@md/field-group:flex-row @md/field-group:items-center @md/field-group:[&>*:not(select[aria-hidden])]:w-auto flex-col [&>*:not(select[aria-hidden])]:w-full [&>.sr-only]:w-auto',
          '@md/field-group:[&>[data-slot=field-label]]:flex-auto',
          '@md/field-group:has-[>[data-slot=field-content]]:items-start @md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px',
        ],
      },
      lookAndFeel: {
        default: null,
        volcanes: 'data-[invalid=true]:text-inherit *:data-[slot=field-label]:w-fit',
        shopify: null,
      },
    },
    defaultVariants: {
      orientation: 'vertical',
      lookAndFeel: 'default',
    },
  }
);

function Field({
  className,
  orientation = 'vertical',
  lookAndFeel,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation, lookAndFeel }), className)}
      {...props}
    />
  );
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        'group/field-content flex flex-1 flex-col gap-1.5 leading-snug',
        className
      )}
      {...props}
    />
  );
}

const fieldLabelVariants = cva(
  'group/field-label peer/field-label flex w-fit gap-2 justif leading-snug group-data-[disabled=true]/field:opacity-50 has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col has-[>[data-slot=field]]:rounded-md has-[>[data-slot=field]]:border [&>[data-slot=field]]:p-4 has-data-[state=checked]:bg-primary/5 has-data-[state=checked]:border-primary dark:has-data-[state=checked]:bg-primary/10',
  {
    variants: {
      lookAndFeel: {
        default: null,
        volcanes: 'font-normal leading-normal text-inherit',
        shopify: null,
      },
    },
    defaultVariants: {
      lookAndFeel: 'default',
    },
  }
);

function FieldLabel({
  className,
  lookAndFeel,
  ...props
}: Omit<React.ComponentProps<typeof Label>, 'lookAndFeel'> & VariantProps<typeof fieldLabelVariants>) {
  return (
    <Label
      data-slot="field-label"
      className={cn(fieldLabelVariants({ lookAndFeel }), className)}
      lookAndFeel={lookAndFeel}
      {...props}
    />
  );
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label"
      className={cn(
        'flex w-fit items-center gap-2 text-sm font-medium leading-snug group-data-[disabled=true]/field:opacity-50',
        className
      )}
      {...props}
    />
  );
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'text-muted-foreground text-sm font-normal leading-normal group-has-[[data-orientation=horizontal]]/field:text-balance',
        'nth-last-2:-mt-1 last:mt-0 [[data-variant=legend]+&]:-mt-1.5',
        '[&>a:hover]:text-primary [&>a]:underline [&>a]:underline-offset-4',
        className
      )}
      {...props}
    />
  );
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={!!children}
      className={cn(
        'relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2',
        className
      )}
      {...props}
    >
      <Separator className="absolute inset-0 top-1/2" />
      {children && (
        <span
          className="bg-background text-muted-foreground relative mx-auto block w-fit px-2"
          data-slot="field-separator-content"
        >
          {children}
        </span>
      )}
    </div>
  );
}

const fieldErrorVariants = cva('text-field-error-fg', {
  variants: {
    lookAndFeel: {
      default: 'text-sm font-semibold',
      volcanes: 'mt-1 block text-[0.9rem] font-bold',
      shopify: 'text-[12.04px] font-normal',
    },
  },
  defaultVariants: {
    lookAndFeel: 'default',
  },
});

function FieldError({
  className,
  children,
  errors,
  lookAndFeel = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldErrorVariants> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = useMemo(() => {
    if (children) {
      return children;
    }

    if (!errors) {
      return null;
    }

    if (errors?.length === 1 && errors[0]?.message) {
      return errors[0].message;
    }

    const messages = [
      ...new Set(errors.flatMap(error => (error?.message ? [error.message] : []))),
    ];

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {messages.map(message => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    );
  }, [children, errors]);

  if (!content) {
    return null;
  }

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn(fieldErrorVariants({ lookAndFeel }), className)}
      {...props}
      data-look-and-feel={lookAndFeel}
    >
      {content}
    </div>
  );
}

export {
  Field,
  FieldLabel,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldContent,
  FieldTitle,
  fieldErrorVariants,
};
